import React from 'react';
import { Row, Col, Card, Form } from 'react-bootstrap';
import { formatPhoneNumber } from 'services/UtilityService';
import { POPULATION_TYPE_OPTIONS, LANGUAGE_OPTIONS } from '../../../lookup/PopulationTypeConstants';
import { ConfigFormLabel, ConfigFormSwitch } from './ConfigFormComponents';

const SelectPlaceholder = ({ children }) => (
  <option value="">{children}</option>
);

export const renderFaxTemplateOptions = (faxTemplates) => {
  if (!faxTemplates || typeof faxTemplates !== 'object' || Array.isArray(faxTemplates)) {
    return null;
  }
  return Object.entries(faxTemplates).map(([key, template]) => {
    const displayName =
      typeof template === 'object' && template !== null && template.displayName
        ? template.displayName
        : typeof template === 'string'
        ? template
        : key;
    const isDisabled = typeof template === 'object' && template !== null && template.disabled === true;
    const displayText = isDisabled ? `${displayName} - Deprecated` : displayName;
    return (
      <option key={key} value={key} disabled={isDisabled}>
        {displayText}
      </option>
    );
  });
};

export const ClientFormSections = ({
  formState,
  errors = {},
  onInputChange,
  onCheckboxChange,
  faxTemplates,
  loadingFaxTemplates = false,
  isReadOnly = false,
  sensitiveFields = [],
  disabledFields = {},
  showValidation = false,
}) => {
  const isSwitchDisabled = (fieldName) =>
    isReadOnly ||
    disabledFields.callActivities ||
    fieldName === 'prescriberCallActivitiesEnabled' ||
    fieldName === 'pharmacyCallActivitiesEnabled';

  return (
    <>
      <Card className="mb-3">
        <Card.Header><Card.Title as="h6">Identity</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Client Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="clientName"
                  value={formState.clientName}
                  onChange={onInputChange}
                  disabled={isReadOnly || disabledFields.clientName}
                  isInvalid={showValidation && !!errors.clientName}
                />
                {showValidation && <Form.Control.Feedback type="invalid">{errors.clientName}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Program Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="programName"
                  value={formState.programName}
                  onChange={onInputChange}
                  disabled={isReadOnly || disabledFields.programName}
                  isInvalid={showValidation && !!errors.programName}
                />
                {showValidation && <Form.Control.Feedback type="invalid">{errors.programName}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={6} className="mt-2">
              <Form.Group>
                <Form.Label>Client Abbreviation <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="clientNameAbbreviation"
                  value={formState.clientNameAbbreviation}
                  onChange={onInputChange}
                  disabled={isReadOnly || disabledFields.clientNameAbbreviation}
                  isInvalid={showValidation && !!errors.clientNameAbbreviation}
                />
                {showValidation && <Form.Control.Feedback type="invalid">{errors.clientNameAbbreviation}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={6} className="mt-2">
              <Form.Group>
                <ConfigFormLabel label="Logo URL" fieldName="logoUrl" sensitiveFields={sensitiveFields} />
                <Form.Control
                  type="text"
                  name="logoUrl"
                  value={formState.logoUrl}
                  onChange={onInputChange}
                  disabled={isReadOnly}
                />
              </Form.Group>
            </Col>
            {formState.customerName !== undefined && (
              <Col md={6} className="mt-2">
                <Form.Group>
                  <Form.Label>Customer Name</Form.Label>
                  <Form.Control type="text" value={formState.customerName} disabled />
                </Form.Group>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><Card.Title as="h6">Contact</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <ConfigFormLabel label="Support Number" fieldName="supportNumber" sensitiveFields={sensitiveFields} />
                <Form.Control
                  type="text"
                  name="supportNumber"
                  value={formatPhoneNumber(formState.supportNumber)}
                  onChange={onInputChange}
                  placeholder="e.g. (555) 555-5555"
                  maxLength={14}
                  disabled={isReadOnly}
                  isInvalid={showValidation && !!errors.supportNumber}
                />
                {showValidation && <Form.Control.Feedback type="invalid">{errors.supportNumber}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <ConfigFormLabel label="Inbound Fax Number" fieldName="faxNumberInbound" sensitiveFields={sensitiveFields} />
                <Form.Control
                  type="text"
                  name="faxNumberInbound"
                  value={formatPhoneNumber(formState.faxNumberInbound)}
                  onChange={onInputChange}
                  placeholder="e.g. (555) 555-5555"
                  maxLength={14}
                  disabled={isReadOnly}
                  isInvalid={showValidation && !!errors.faxNumberInbound}
                />
                {showValidation && <Form.Control.Feedback type="invalid">{errors.faxNumberInbound}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><Card.Title as="h6">Call Activities</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <ConfigFormSwitch
                label="Prescribers"
                fieldName="prescriberCallActivitiesEnabled"
                checked={formState.prescriberCallActivitiesEnabled}
                onChange={onCheckboxChange}
                id="prescriber-call-activities-switch"
                disabled={isSwitchDisabled('prescriberCallActivitiesEnabled')}
                sensitiveFields={sensitiveFields}
              />
            </Col>
            <Col md={6}>
              <ConfigFormSwitch
                label="Pharmacies"
                fieldName="pharmacyCallActivitiesEnabled"
                checked={formState.pharmacyCallActivitiesEnabled}
                onChange={onCheckboxChange}
                id="pharmacy-call-activities-switch"
                disabled={isSwitchDisabled('pharmacyCallActivitiesEnabled')}
                sensitiveFields={sensitiveFields}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><Card.Title as="h6">Programs</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <ConfigFormSwitch label="Polypharmacy" fieldName="polypharmacyEnabled" checked={formState.polypharmacyEnabled} onChange={onCheckboxChange} id="polypharmacy-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
            </Col>
            <Col md={6}>
              <ConfigFormSwitch label="Deprescription Confirmations" fieldName="deprescriptionConfirmationEnabled" checked={formState.deprescriptionConfirmationEnabled} onChange={onCheckboxChange} id="deprescription-confirmation-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><Card.Title as="h6">Other</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <ConfigFormSwitch label="Custom Switches" fieldName="hasCustomSwitches" checked={formState.hasCustomSwitches} onChange={onCheckboxChange} id="custom-switches-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
            </Col>
            <Col md={6}>
              <ConfigFormSwitch label="Prescriber Emails" fieldName="isPrescriberEmailEnabled" checked={formState.isPrescriberEmailEnabled} onChange={onCheckboxChange} id="prescriber-email-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <ConfigFormSwitch label="RX Advocate" fieldName="isRxAdvocateEnabled" checked={formState.isRxAdvocateEnabled} onChange={onCheckboxChange} id="rx-advocate-enabled-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><Card.Title as="h6">Pricing</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <ConfigFormSwitch label="Use Price Factors" fieldName="useNvpSourcePricing" checked={formState.useNvpSourcePricing} onChange={onCheckboxChange} id="use-nvp-source-pricing-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><Card.Title as="h6">STARS</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <ConfigFormSwitch label="Exclude STARS Medications" fieldName="excludeStarSwitches" checked={formState.excludeStarSwitches} onChange={onCheckboxChange} id="exclude-star-switches-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
            </Col>
            <Col md={6}>
              <ConfigFormSwitch label="Hide Deprescribe Suggestions" fieldName="excludeStarDeprescriptions" checked={formState.excludeStarDeprescriptions} onChange={onCheckboxChange} id="exclude-star-deprescriptions-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><Card.Title as="h6">Program defaults</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <ConfigFormLabel label={<>Language <span className="text-danger">*</span></>} fieldName="language" sensitiveFields={sensitiveFields} />
                <Form.Control as="select" name="language" value={formState.language || ''} onChange={onInputChange} disabled={isReadOnly} isInvalid={showValidation && !!errors.language}>
                  <SelectPlaceholder>Select a language</SelectPlaceholder>
                  {LANGUAGE_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Form.Control>
                {showValidation && <Form.Control.Feedback type="invalid">{errors.language}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <ConfigFormLabel label={<>Fax Template <span className="text-danger">*</span></>} fieldName="faxTemplate" sensitiveFields={sensitiveFields} />
                <Form.Control as="select" name="faxTemplate" value={formState.faxTemplate || ''} onChange={onInputChange} disabled={isReadOnly} isInvalid={showValidation && !!errors.faxTemplate}>
                  <SelectPlaceholder>Select a template</SelectPlaceholder>
                  {renderFaxTemplateOptions(faxTemplates)}
                  {!loadingFaxTemplates && faxTemplates && Object.keys(faxTemplates).length === 0 && (
                    <option value="" disabled>No templates available</option>
                  )}
                </Form.Control>
                {showValidation && <Form.Control.Feedback type="invalid">{errors.faxTemplate}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <ConfigFormLabel label={<>Population Type <span className="text-danger">*</span></>} fieldName="populationType" sensitiveFields={sensitiveFields} />
                <Form.Control as="select" name="populationType" value={formState.populationType || ''} onChange={onInputChange} disabled={isReadOnly} isInvalid={showValidation && !!errors.populationType}>
                  <SelectPlaceholder>Select population type</SelectPlaceholder>
                  {POPULATION_TYPE_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Form.Control>
                {showValidation && <Form.Control.Feedback type="invalid">{errors.populationType}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};
